import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  AuditService,
  GetAuditEventRequestSchema,
  ListAuditEventsRequestSchema,
} from '../gen/audit/v1/audit_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

/**
 * Hook to list audit events with optional resource filters.
 * Results are ordered by creation time descending (newest first).
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param resourceKind - Optional resource kind filter (e.g. "tenant", "user", "deployment")
 * @param resourceId - Optional resource ID filter
 * @returns Query result with list of audit events
 */
export function useListAuditEvents(
  token?: string | null,
  resourceKind?: string,
  resourceId?: string,
  refetchInterval?: number,
  pageSize?: number,
) {
  return useQuery({
    queryKey: ['audit', 'list', resourceKind ?? '', resourceId ?? '', !!token, pageSize ?? 0],
    queryFn: async () => {
      const client = createClient(AuditService, transport(token));
      const request = create(ListAuditEventsRequestSchema, {
        ...(resourceKind ? { resourceKind } : {}),
        ...(resourceId ? { resourceId } : {}),
        ...(pageSize !== undefined ? { pageSize } : {}),
      });
      return await client.listAuditEvents(request);
    },
    enabled: !!token,
    refetchInterval,
  });
}

/**
 * Hook to list audit events with infinite scroll / "load more" support.
 * Uses cursor-based pagination via page_token. Results are newest first.
 *
 * @param token - Auth0 access token
 * @param resourceKind - Optional resource kind filter (e.g. "tenant", "workspace")
 * @param resourceId - Optional resource ID filter
 * @param pageSize - Number of events per page (default 20)
 */
export function useInfiniteListAuditEvents(
  token?: string | null,
  resourceKind?: string,
  resourceId?: string,
  pageSize = 20,
) {
  return useInfiniteQuery({
    queryKey: ['audit', 'list', 'infinite', resourceKind ?? '', resourceId ?? '', !!token],
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      const client = createClient(AuditService, transport(token));
      const request = create(ListAuditEventsRequestSchema, {
        ...(resourceKind ? { resourceKind } : {}),
        ...(resourceId ? { resourceId } : {}),
        pageSize,
        ...(pageParam ? { pageToken: pageParam } : {}),
      });
      return await client.listAuditEvents(request);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled: !!token,
  });
}

/**
 * Hook to retrieve a single audit event by ID.
 *
 * @param id - The audit event ID to retrieve
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with audit event data
 */
export function useGetAuditEvent(id: string, token?: string | null) {
  return useQuery({
    queryKey: ['audit', id, !!token],
    queryFn: async () => {
      const client = createClient(AuditService, transport(token));
      const request = create(GetAuditEventRequestSchema, { id });
      return await client.getAuditEvent(request);
    },
    enabled: !!id && !!token,
  });
}
